import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import { leadsAPI } from '../../utils/api';
import { Plus, Trash2, Tag, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_OPTIONS = [
  { value: 'meta', label: 'Meta (Facebook/Instagram)' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'google', label: 'Google Ads' },
  { value: 'instagram', label: 'Instagram Reels/Posts' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-In' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const PLATFORM_COLORS = {
  meta: 'bg-blue-100 text-blue-700',
  linkedin: 'bg-sky-100 text-sky-700',
  google: 'bg-red-100 text-red-700',
  instagram: 'bg-pink-100 text-pink-700',
  youtube: 'bg-rose-100 text-rose-700',
  whatsapp: 'bg-green-100 text-green-700',
  referral: 'bg-amber-100 text-amber-700',
  walk_in: 'bg-violet-100 text-violet-700',
  website: 'bg-teal-100 text-teal-700',
  other: 'bg-slate-100 text-slate-700',
};

export default function LeadSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPlatform, setNewPlatform] = useState('');

  useEffect(() => { fetchSources(); }, []);

  const fetchSources = async () => {
    try {
      const res = await leadsAPI.getSources();
      setSources(res.data.sources || []);
    } catch {
      toast.error('Failed to load lead sources');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await leadsAPI.createSource({ name, platform: newPlatform || null });
      toast.success(`"${name}" added`);
      setNewName('');
      setNewPlatform('');
      await fetchSources();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add source');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try {
      await leadsAPI.deleteSource(id);
      setSources(prev => prev.filter(s => s.id !== id));
      toast.success('Removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Lead Sources</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage platforms and channels leads come from</p>
          </div>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* Add form */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">Add New Source</h2>
            <div className="flex gap-3">
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Source name (e.g. Meta Campaign Q2)"
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none bg-white"
              />
              <select
                value={newPlatform}
                onChange={e => setNewPlatform(e.target.value)}
                className="w-44 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none bg-white"
              >
                <option value="">Platform (optional)</option>
                {PLATFORM_OPTIONS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={saving || !newName.trim()}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium whitespace-nowrap"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add
              </button>
            </div>
          </div>

          {/* Sources list */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
              <Tag size={16} className="text-teal-600" />
              <span className="font-semibold text-slate-800 text-sm">Configured Sources</span>
              <span className="ml-auto text-xs text-slate-400">{sources.length} total</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-slate-400" />
              </div>
            ) : sources.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Tag size={32} className="mb-2 text-slate-300" />
                <p className="text-sm">No sources yet. Add your first one above.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {sources.map(source => (
                  <div key={source.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                        <Tag size={14} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{source.name}</p>
                        {source.platform && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLATFORM_COLORS[source.platform] || PLATFORM_COLORS.other}`}>
                            {PLATFORM_OPTIONS.find(p => p.value === source.platform)?.label || source.platform}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(source.id, source.name)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Lead sources are <strong>only visible to Super Admin, Admin, and Marketing Manager</strong>. Sales consultants, managers, and process managers cannot see the source of a lead — this prevents bias in how they approach leads.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
